import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  Eye,
  Download,
  Filter,
  Calendar,
  Activity,
  Zap,
  Target,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  Search,
  FileText,
  Settings,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import CountUp from '../../components/CountUp';
import '../../styles/background-effects.css';


const AnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'usage' | 'performance' | 'reports'>('overview');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [loading, setLoading] = useState(false);

  // Données simulées pour les statistiques
  const analyticsData = {
    overview: {
      totalUsers: 1247,
      activeUsers: 892,
      sessions: 3456,
      avgSessionDuration: '12:34',
      bounceRate: 23.4,
      conversionRate: 8.7,
      pageViews: 15678,
      newUsers: 234
    },
    userActivity: [
      { time: '00:00', users: 12 },
      { time: '04:00', users: 8 },
      { time: '08:00', users: 156 },
      { time: '12:00', users: 234 },
      { time: '16:00', users: 198 },
      { time: '20:00', users: 145 },
      { time: '24:00', users: 67 }
    ],
    topPages: [
      { page: '/workspace', views: 2341, users: 892, avgTime: '8:45' },
      { page: '/workspace/documents', views: 1876, users: 567, avgTime: '12:23' },
      { page: '/workspace/analytics', views: 1234, users: 234, avgTime: '15:12' },
      { page: '/workspace/evaluation', views: 987, users: 189, avgTime: '6:34' },
      { page: '/workspace/content', views: 765, users: 156, avgTime: '9:21' }
    ],
    deviceStats: [
      { device: 'Desktop', users: 567, percentage: 63.6 },
      { device: 'Mobile', users: 234, percentage: 26.2 },
      { device: 'Tablet', users: 91, percentage: 10.2 }
    ],
    userJourney: [
      { step: 'Connexion', users: 100, dropoff: 0 },
      { step: 'Dashboard', users: 95, dropoff: 5 },
      { step: 'Documents', users: 78, dropoff: 17 },
      { step: 'Recherche', users: 56, dropoff: 22 },
      { step: 'Téléchargement', users: 34, dropoff: 22 }
    ],
    performanceMetrics: [
      { metric: 'Temps de chargement', value: '1.2s', status: 'good', change: '-15%' },
      { metric: 'Taux d\'erreur', value: '0.3%', status: 'good', change: '-8%' },
      { metric: 'Disponibilité', value: '99.9%', status: 'excellent', change: '+0.1%' },
      { metric: 'Satisfaction utilisateur', value: '4.7/5', status: 'excellent', change: '+0.2' }
    ],
    recentActivity: [
      { user: 'Marie Dubois', action: 'Connexion', time: '2 min', location: 'Paris, FR' },
      { user: 'Jean Martin', action: 'Upload document', time: '5 min', location: 'Lyon, FR' },
      { user: 'Sophie Bernard', action: 'Recherche IA', time: '8 min', location: 'Marseille, FR' },
      { user: 'Pierre Moreau', action: 'Export rapport', time: '12 min', location: 'Toulouse, FR' },
      { user: 'Claire Petit', action: 'Création projet', time: '15 min', location: 'Nice, FR' }
    ]
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-600 bg-green-100';
      case 'good': return 'text-blue-600 bg-blue-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent': return <CheckCircle className="w-4 h-4" />;
      case 'good': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertCircle className="w-4 h-4" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <div className="space-y-6 content-above-aurora">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics & Monitoring</h1>
            <p className="text-gray-600">Surveillance des performances et analyse d'utilisation</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
              <option value="1y">1 an</option>
            </select>
            <Button variant="outline" icon={<Download className="w-4 h-4" />}>
              Exporter
            </Button>
            <Button variant="outline" icon={<RefreshCw className="w-4 h-4" />}>
              Actualiser
            </Button>
          </div>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Vue d\'ensemble', icon: BarChart3 },
              { id: 'users', name: 'Utilisateurs', icon: Users },
              { id: 'usage', name: 'Utilisation', icon: Activity },
              { id: 'performance', name: 'Performance', icon: Zap },
              { id: 'reports', name: 'Rapports', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Métriques principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Utilisateurs Actifs</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.activeUsers} duration={2} delay={0.2} separator="," className="text-2xl font-bold text-gray-900" />
                      </p>
                      <p className="text-xs text-green-600 mt-1">+12% vs mois dernier</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Sessions</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.sessions} duration={2} delay={0.4} separator="," className="text-2xl font-bold text-gray-900" />
                      </p>
                      <p className="text-xs text-green-600 mt-1">+8% vs mois dernier</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Durée Moyenne</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={parseFloat(analyticsData.overview.avgSessionDuration.replace('min', ''))} duration={2} delay={0.6} className="text-2xl font-bold text-gray-900" />min
                      </p>
                      <p className="text-xs text-blue-600 mt-1">+2min vs mois dernier</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Taux de Conversion</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.conversionRate} duration={2} delay={0.8} className="text-2xl font-bold text-gray-900" />%
                      </p>
                      <p className="text-xs text-green-600 mt-1">+1.2% vs mois dernier</p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Graphique d'activité */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activité des Utilisateurs (24h)</h3>
                <div className="h-64 flex items-end justify-between space-x-2">
                  {analyticsData.userActivity.map((data, index) => (
                    <div key={index} className="flex flex-col items-center space-y-2">
                      <motion.div
                        className="w-8 bg-blue-500 rounded-t"
                        initial={{ height: 0 }}
                        animate={{ height: `${(data.users / 250) * 200}px` }}
                        transition={{ 
                          duration: 0.8, 
                          delay: index * 0.1,
                          ease: "easeOut"
                        }}
                      ></motion.div>
                      <span className="text-xs text-gray-600">{data.time}</span>
                      <span className="text-xs font-medium text-gray-900">{data.users}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Pages les plus visitées */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pages les Plus Visitées</h3>
                <div className="space-y-4">
                  {analyticsData.topPages.map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{page.page}</p>
                          <p className="text-sm text-gray-500">{page.users} utilisateurs uniques</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">{page.views.toLocaleString()} vues</p>
                        <p className="text-sm text-gray-500">Temps moyen: {page.avgTime}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Statistiques utilisateurs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Utilisateurs</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.totalUsers} duration={2} delay={0.3} separator="," className="text-2xl font-bold text-gray-900" />
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Nouveaux Utilisateurs</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.newUsers} duration={1.5} delay={0.5} className="text-2xl font-bold text-gray-900" />
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-600" />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Taux de Rétention</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={78} duration={1.5} delay={0.7} className="text-2xl font-bold text-gray-900" />%
                      </p>
                    </div>
                    <Target className="w-8 h-8 text-purple-600" />
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Répartition par appareil */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Appareil</h3>
                <div className="space-y-4">
                  {analyticsData.deviceStats.map((device, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {device.device === 'Desktop' && <Monitor className="w-5 h-5 text-blue-600" />}
                        {device.device === 'Mobile' && <Smartphone className="w-5 h-5 text-green-600" />}
                        {device.device === 'Tablet' && <Tablet className="w-5 h-5 text-purple-600" />}
                        <span className="font-medium text-gray-900">{device.device}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="bg-blue-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${device.percentage}%` }}
                            transition={{ 
                              duration: 1, 
                              delay: 0.5,
                              ease: "easeOut"
                            }}
                          ></motion.div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-12 text-right">
                          {device.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Activité récente */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activité Récente</h3>
                <div className="space-y-3">
                  {analyticsData.recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">
                            {activity.user.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{activity.user}</p>
                          <p className="text-sm text-gray-500">{activity.action}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{activity.time}</p>
                        <p className="text-xs text-gray-500">{activity.location}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="space-y-6">
            {/* Métriques d'utilisation */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Pages Vues</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.pageViews} duration={2} delay={0.4} separator="," className="text-2xl font-bold text-gray-900" />
                      </p>
                    </div>
                    <Eye className="w-8 h-8 text-blue-600" />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Taux de Rebond</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={analyticsData.overview.bounceRate} duration={1.5} delay={0.6} className="text-2xl font-bold text-gray-900" />%
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-600" />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Recherches IA</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={1234} duration={2} delay={0.8} separator="," className="text-2xl font-bold text-gray-900" />
                      </p>
                    </div>
                    <Search className="w-8 h-8 text-purple-600" />
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Card className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Documents Téléchargés</p>
                      <p className="text-2xl font-bold text-gray-900">
                        <CountUp to={567} duration={1.5} delay={1.0} className="text-2xl font-bold text-gray-900" />
                      </p>
                    </div>
                    <Download className="w-8 h-8 text-green-600" />
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Parcours utilisateur */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Parcours Utilisateur</h3>
                <div className="space-y-4">
                  {analyticsData.userJourney.map((step, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                        </div>
                        {index < analyticsData.userJourney.length - 1 && (
                          <div className="w-0.5 h-8 bg-gray-300 mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">{step.step}</h4>
                          <span className="text-sm text-gray-600">{step.users} utilisateurs</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="bg-blue-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${(step.users / 100) * 100}%` }}
                            transition={{ 
                              duration: 1.2, 
                              delay: 0.3,
                              ease: "easeOut"
                            }}
                          ></motion.div>
                        </div>
                        {step.dropoff > 0 && (
                          <p className="text-xs text-red-600 mt-1">-{step.dropoff}% d'abandon</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Métriques de performance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {analyticsData.performanceMetrics.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-900">{metric.metric}</h3>
                      <div className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${getStatusColor(metric.status)}`}>
                        {getStatusIcon(metric.status)}
                        {metric.status}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">{metric.value}</span>
                      <span className={`text-sm font-medium ${
                        metric.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {metric.change}
                      </span>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Monitoring en temps réel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monitoring Temps Réel</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="font-medium text-gray-900">Serveur Principal</p>
                    <p className="text-sm text-green-600">En ligne</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="font-medium text-gray-900">Base de Données</p>
                    <p className="text-sm text-green-600">En ligne</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="font-medium text-gray-900">API IA</p>
                    <p className="text-sm text-green-600">En ligne</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Rapports Disponibles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: 'Rapport d\'Utilisation Mensuel', type: 'PDF', size: '2.3 MB', date: '2024-01-15' },
                    { name: 'Analyse des Performances', type: 'Excel', size: '1.8 MB', date: '2024-01-14' },
                    { name: 'Statistiques Utilisateurs', type: 'CSV', size: '945 KB', date: '2024-01-13' },
                    { name: 'Audit de Sécurité', type: 'PDF', size: '3.1 MB', date: '2024-01-12' },
                    { name: 'Métriques IA', type: 'JSON', size: '567 KB', date: '2024-01-11' },
                    { name: 'Rapport de Conformité', type: 'PDF', size: '4.2 MB', date: '2024-01-10' }
                  ].map((report, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm">{report.name}</h4>
                        <span className="text-xs text-gray-500">{report.type}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{report.size}</span>
                        <span>{report.date}</span>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <Button variant="outline" size="sm" className="flex-1">
                          <Eye className="w-3 h-3 mr-1" />
                          Voir
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1">
                          <Download className="w-3 h-3 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
